import { useEffect, useRef } from "react";
import { getLanguageById } from "../config/languages";
import { clearPlaygroundDraft, savePlaygroundDraft } from "./playground-draft-storage";

const DRAFT_DEBOUNCE_MS = 800;

/**
 * Debounced localStorage persistence for unsaved scratch work. Disabled for
 * saved snippets and shared views (those persist via the API). Pristine
 * content (starter code, empty stdin) clears the draft instead.
 *
 * `PG-PWA01` added two guarantees. The pending write is flushed when the
 * page is hidden or unloading (`pagehide`, `visibilitychange`), so a tab
 * closed inside the debounce window keeps its last keystrokes; and a write
 * the device refused (quota, a blocked storage) is reported once through
 * `onPersistFailure` instead of vanishing.
 */
export function useScratchDraftPersistence(args: {
  enabled: boolean;
  languageId: string;
  sourceCode: string;
  stdin: string;
  /** Called once per mount the first time a draft could not be stored on this device. */
  onPersistFailure?: () => void;
}): void {
  const { enabled, languageId, sourceCode, stdin, onPersistFailure } = args;
  const failureReportedRef = useRef(false);
  const onPersistFailureRef = useRef(onPersistFailure);
  onPersistFailureRef.current = onPersistFailure;

  /** The write the debounce is holding, so a flush can run it early. */
  const pendingRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) {
      pendingRef.current = null;
      return;
    }
    const lang = getLanguageById(languageId);
    if (!lang) return;
    const persist = () => {
      pendingRef.current = null;
      if (sourceCode === lang.defaultCode && stdin === "") {
        clearPlaygroundDraft(lang.id);
        return;
      }
      const stored = savePlaygroundDraft(lang.id, { sourceCode, stdin });
      if (!stored && !failureReportedRef.current) {
        failureReportedRef.current = true;
        onPersistFailureRef.current?.();
      }
    };
    pendingRef.current = persist;
    const timer = window.setTimeout(persist, DRAFT_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      // A pending write is not lost on cleanup: the next effect run replaces it
      // with the newer buffer, and unmount is covered by the flush below.
    };
  }, [enabled, languageId, sourceCode, stdin]);

  useEffect(() => {
    const flush = () => {
      pendingRef.current?.();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      flush();
    };
  }, []);
}

/**
 * Writes the current buffer now, ahead of the debounce. The reload action of
 * the "cached copy" state calls this before `location.reload()`.
 */
export function flushPlaygroundDraft(args: {
  languageId: string;
  sourceCode: string;
  stdin: string;
}): boolean {
  const lang = getLanguageById(args.languageId);
  if (!lang) return false;
  if (args.sourceCode === lang.defaultCode && args.stdin === "") {
    clearPlaygroundDraft(lang.id);
    return true;
  }
  return savePlaygroundDraft(lang.id, { sourceCode: args.sourceCode, stdin: args.stdin });
}
